import type { AdminProposalListItem, AdminProposalListResponse } from '@inflip/contracts/admin';
import {
  IP_RIGHT_LABELS,
  PROPOSAL_STATUS_LABELS,
  ProposalStatus,
  rateBpLabel,
} from '@inflip/shared';
import { Link } from '@tanstack/react-router';
import type { ColumnDef } from '@tanstack/react-table';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { won } from '../components/confirm-deposit-dialog';
import { DataTable } from '../components/data-table';
import { ProposalRounds } from '../components/order-proposal-history';
import {
  Button,
  Chip,
  Dialog,
  EmptyState,
  ErrorBanner,
  PageHeader,
  Pager,
  PromptDialog,
  inputCls,
} from '../components/ui';
import { api } from '../lib/api-client';
import { useAdminAuth } from '../lib/auth-context';
import { formatDateTime } from '../lib/time-zone';
import { useListViewState } from '../lib/use-list-view-state';

const PAGE_SIZE = 20;

/** 상태는 컬러 텍스트 — 진행 중 amber, 성립 emerald, 그 밖의 종료는 muted */
const STATUS_TEXT: Record<ProposalStatus, string> = {
  PENDING: 'text-amber-600',
  ACCEPTED: 'text-emerald-600',
  DECLINED: 'text-muted-foreground',
  WITHDRAWN: 'text-muted-foreground',
  EXPIRED: 'text-muted-foreground',
  CLOSED: 'text-rose-600',
};

type DialogState =
  | null
  | { kind: 'history'; item: AdminProposalListItem }
  | { kind: 'close'; item: AdminProposalListItem };

/** 당사자가 끝낸 협의는 누가 끝냈는지 — 운영자 종료·만료는 상태로 충분하다 */
function closedByLabel(p: AdminProposalListItem): string | null {
  if (p.status === ProposalStatus.CLOSED || !p.closedBy) return null;
  const who = p.closedBy === p.buyer.id ? '구매자' : p.closedBy === p.seller.id ? '판매자' : null;
  if (!who) return null;
  return p.status === ProposalStatus.WITHDRAWN ? `${who}가 제안 취소` : `${who}가 거절`;
}

/**
 * 조건 협의 — 주문이 되기 전 협의를 본다. 기본은 진행 중.
 * 진행 중인 협의는 [협의 닫기]로 사유를 남기고 끝낼 수 있다 (당사자 둘 다에게 사유와 함께 알림).
 */
export function ProposalsPage() {
  const { session } = useAdminAuth();
  const [filter, setFilter] = useListViewState<ProposalStatus | ''>(
    'status',
    ProposalStatus.PENDING,
  );
  const [searchTerm, setSearchTerm] = useListViewState('q', '');
  const [q, setQ] = useState(searchTerm);
  useEffect(() => setQ(searchTerm), [searchTerm]);
  const [page, setPage] = useListViewState('page', 1);
  const [result, setResult] = useState<AdminProposalListResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [dialog, setDialog] = useState<DialogState>(null);

  const load = useCallback(() => {
    if (!session) return Promise.resolve();
    return api
      .listProposals(session.accessToken, {
        page,
        pageSize: PAGE_SIZE,
        ...(filter ? { status: filter } : {}),
        ...(searchTerm ? { q: searchTerm } : {}),
      })
      .then(setResult)
      .catch((err) => setError(err instanceof Error ? err.message : String(err)));
  }, [session, filter, page, searchTerm]);

  useEffect(() => {
    void load();
  }, [load]);

  async function run(fn: () => Promise<unknown>) {
    if (pending) return;
    setPending(true);
    setError(null);
    try {
      await fn();
      setDialog(null);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      // 그사이 당사자가 수락·취소했거나 만료됐을 수 있다 — 지금 상태로 다시 그린다
      setDialog(null);
      await load();
    } finally {
      setPending(false);
    }
  }

  const columns = useMemo<ColumnDef<AdminProposalListItem>[]>(
    () => [
      {
        id: 'status',
        header: '상태',
        size: 100,
        cell: ({ row }) => {
          const p = row.original;
          const by = closedByLabel(p);
          return (
            <>
              <span className={`text-xs font-bold ${STATUS_TEXT[p.status]}`}>
                {PROPOSAL_STATUS_LABELS[p.status]}
              </span>
              {by && <div className="mt-0.5 text-[11px] text-muted-foreground">{by}</div>}
            </>
          );
        },
      },
      {
        id: 'listing',
        header: '상품 · 권리',
        cell: ({ row }) => (
          <>
            <span className="block truncate text-sm font-semibold">
              {row.original.listingTitle ?? '(삭제됨)'}
            </span>
            <span className="text-xs text-muted-foreground">
              {IP_RIGHT_LABELS[row.original.rightType]}
            </span>
          </>
        ),
      },
      {
        id: 'parties',
        header: '구매자 → 판매자',
        cell: ({ row }) => {
          const p = row.original;
          return (
            <>
              {[
                { label: '구매', party: p.buyer },
                { label: '판매', party: p.seller },
              ].map(({ label, party }) => (
                <div key={label} className="flex items-baseline gap-1.5 truncate text-sm">
                  <span className="text-[11px] text-muted-foreground">{label}</span>
                  <Link
                    to="/users/$userId"
                    params={{ userId: party.id }}
                    className="truncate font-semibold text-brand hover:underline"
                  >
                    {party.name ?? '(탈퇴)'}
                  </Link>
                </div>
              ))}
            </>
          );
        },
      },
      {
        id: 'terms',
        header: '현재 조건',
        cell: ({ row }) => {
          const p = row.original;
          const turn = p.proposedBy === p.buyer.id ? '판매자' : '구매자';
          return (
            <>
              <div className="whitespace-nowrap font-bold tabular-nums">
                {won(p.terms.upfrontAmount)}
                {p.terms.royaltyRateBp ? ` + 매출 ${rateBpLabel(p.terms.royaltyRateBp)}` : ''}
                <span className="ml-1 text-xs font-medium text-muted-foreground">
                  / {p.terms.durationMonths === 0 ? '1회' : `${p.terms.durationMonths}개월`}
                </span>
              </div>
              <div className="text-xs text-muted-foreground">
                {p.round}차 제안
                {p.status === ProposalStatus.PENDING && ` · ${turn} 응답 차례`}
              </div>
            </>
          );
        },
      },
      {
        id: 'when',
        header: '기한 · 종료',
        cell: ({ row }) => {
          const p = row.original;
          if (p.status === ProposalStatus.PENDING)
            return (
              <div className="whitespace-nowrap text-xs">
                {p.expiresAt ? `${formatDateTime(p.expiresAt)}까지` : '기한 없음'}
              </div>
            );
          return (
            <>
              <div className="whitespace-nowrap text-xs text-muted-foreground">
                {p.decidedAt ? formatDateTime(p.decidedAt) : '—'}
              </div>
              {p.closeReason && (
                <div className="mt-0.5 max-w-64 truncate text-xs" title={p.closeReason}>
                  사유 · {p.closeReason}
                </div>
              )}
              {p.orderId && (
                <Link
                  to="/orders/$orderId"
                  params={{ orderId: p.orderId }}
                  className="font-mono text-xs font-medium text-brand hover:underline"
                >
                  주문 {p.orderId.slice(0, 8)}
                </Link>
              )}
            </>
          );
        },
      },
      {
        id: 'updatedAt',
        header: '최근 변경',
        cell: ({ row }) => formatDateTime(row.original.updatedAt),
        meta: { tdClass: 'whitespace-nowrap text-xs text-muted-foreground' },
      },
      {
        id: 'actions',
        header: '처리',
        minSize: 170,
        meta: { align: 'right' },
        cell: ({ row }) => (
          <div className="flex justify-end gap-1.5">
            <Button
              size="sm"
              variant="secondary"
              onClick={() => setDialog({ kind: 'history', item: row.original })}
            >
              협의 내역
            </Button>
            {row.original.status === ProposalStatus.PENDING && (
              <Button
                size="sm"
                variant="danger"
                disabled={pending}
                onClick={() => setDialog({ kind: 'close', item: row.original })}
              >
                협의 닫기
              </Button>
            )}
          </div>
        ),
      },
    ],
    [pending],
  );

  if (!session) return null;
  const token = session.accessToken;
  const rows = result?.items ?? [];

  return (
    <main className="w-full px-6 py-8">
      <PageHeader
        title="조건 협의"
        description={
          result
            ? `${result.total.toLocaleString('ko-KR')}건 · 주문이 되기 전 구매자·판매자의 조건 협의`
            : '주문이 되기 전 구매자·판매자의 조건 협의'
        }
        actions={
          <form
            className="flex gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              setPage(1);
              setSearchTerm(q.trim());
            }}
          >
            <input
              className={`w-72 ${inputCls}`}
              placeholder="협의 번호 / 상품 / 구매자·판매자 이름·이메일"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
            <Button type="submit">검색</Button>
          </form>
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-1.5">
        <Chip
          active={filter === ''}
          onClick={() => {
            setPage(1);
            setFilter('');
          }}
        >
          전체
        </Chip>
        {Object.values(ProposalStatus).map((s) => (
          <Chip
            key={s}
            active={filter === s}
            onClick={() => {
              setPage(1);
              setFilter(s);
            }}
          >
            {PROPOSAL_STATUS_LABELS[s]}
          </Chip>
        ))}
      </div>

      {error && <ErrorBanner message={error} />}

      {result && rows.length === 0 ? (
        <EmptyState message="해당 상태의 협의가 없어요." />
      ) : (
        <DataTable data={rows} columns={columns} getRowId={(r) => r.id} />
      )}

      {result && <Pager page={result.page} totalPages={result.totalPages} onChange={setPage} />}

      {dialog?.kind === 'history' && (
        <Dialog
          wide
          label="협의 내역"
          title={`협의 내역 · ${dialog.item.listingTitle ?? '(삭제됨)'}`}
          onClose={() => setDialog(null)}
        >
          <p className="text-xs text-muted-foreground">
            차수마다 그때 남긴 메시지와 바로 앞 제안에서 바뀐 조건을 보여 줘요.
          </p>
          <ProposalRounds
            history={dialog.item.history}
            buyer={dialog.item.buyer}
            seller={dialog.item.seller}
          />
        </Dialog>
      )}
      {dialog?.kind === 'close' && (
        <PromptDialog
          title="협의를 닫을까요?"
          description={`"${dialog.item.listingTitle ?? '(삭제됨)'}" 협의를 운영자 종료로 끝내요. 구매자·판매자 둘 다에게 사유와 함께 알림이 가고, 이 협의로는 더 수락·수정할 수 없어요. 새 협의는 구매자가 다시 제안해 시작할 수 있어요.`}
          placeholder="종료 사유 (5자 이상, 당사자에게 보여요)"
          submitLabel="협의 닫기"
          danger
          busy={pending}
          minLength={5}
          onSubmit={(reason) =>
            void run(() => api.closeProposal(token, dialog.item.id, { reason }))
          }
          onClose={() => setDialog(null)}
        />
      )}
    </main>
  );
}
